/* eslint-disable react/display-name */
import MaterialTable from '@material-table/core'
import ClearSharpIcon from '@mui/icons-material/ClearSharp'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import { useTheme } from '@mui/material/styles'
import { AxiosResponse } from 'axios'
import React, { FC } from 'react'
import { UseQueryResult, useMutation } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import {
  FixedRoute,
  FixedRoutes,
  Supplier,
} from '../../../../api/supplier/suppliers.types'
import { suppliersAPI } from '../../../../api/supplier/suppliersAPI'
import { NotificationType } from './FixedRoutes'

interface FixedRouteTableProps {
  setNotify: React.Dispatch<React.SetStateAction<NotificationType>>
  supplierFixedRoutes: UseQueryResult<AxiosResponse<FixedRoutes>, unknown>
  setFixedRoute: (routeData: any) => void
  setConfirmDialog: React.Dispatch<React.SetStateAction<any>>
}

const FixedRoutesTable: FC<FixedRouteTableProps> = (props) => {
  const {
    setNotify,
    supplierFixedRoutes,
    setConfirmDialog,
    setFixedRoute,
  } = props
  const supplier: Supplier = useSelector(
    (state: any) => state.suppliers.supplier,
  )
  const theme = useTheme() as any
  const headerBackgroundColor =
    theme.components?.journeyStatus?.headerColour ?? theme.palette.grey[300]
  const headerTextColor =
    theme.components?.journeyStatus?.headerTextColour ??
    theme.palette.getContrastText(headerBackgroundColor)

  const { mutateAsync: deleteMutate } = useMutation({
    mutationFn: (payload: any) =>
      suppliersAPI.deleteSupplierFixedRoute(
        payload.supplierId,
        payload.fixedRoute,
      ),
  })

  const routes = supplierFixedRoutes.data?.data.value

  const onDelete = async (rowData: any) => {
    setConfirmDialog((currentDialog: any) => ({
      ...currentDialog,
      isOpen: false,
    }))
    const payload = {
      fixedRoute: rowData.id,
      supplierId: supplier.id,
    }
    await deleteMutate(payload)
    await supplierFixedRoutes.refetch()
    setNotify({
      isOpen: true,
      message: `Route "${rowData.routeName}" deleted successfully`,
      type: 'success',
    })
  }

  const formatLocation = (location: any) => {
    if (!location) return ''
    if (location.isPartial) {
      return `${location.postCode} (Partial)`
    }
    return [location.addressLine1, location.town].filter(Boolean).join(', ')
  }

  const mapExceptionsToArray = (route: FixedRoute) => {
    const data = {
      id: route.id,
      routeName: route.name,
      pickup: formatLocation(route.pickup),
      dropoff: formatLocation(route.dropOff),
    }
    return data
  }

  const tableData = () => {
    const tblData: Array<object> = []
    if (routes) {
      routes.forEach((route) => {
        const mapped = mapExceptionsToArray(route)
        tblData.push(mapped)
      })
    }
    return tblData
  }

  return (
    <MaterialTable
      title="Fixed Routes"
      columns={[
        { title: 'id', field: 'id', hidden: true },
        {
          title: 'Route Name',
          field: 'routeName',
          cellStyle: { width: 400, maxWidth: 400 },
          headerStyle: { width: 400, maxWidth: 400 },
        },
        {
          title: 'Pickup Location',
          field: 'pickup',
          cellStyle: { width: 300, maxWidth: 300 },
          headerStyle: { width: 300, maxWidth: 300 },
        },
        {
          title: 'Drop Off Location',
          field: 'dropoff',
          cellStyle: { width: 300, maxWidth: 300 },
          headerStyle: { width: 300, maxWidth: 300 },
        },
      ]}
      data={tableData()}
      options={{
        headerStyle: {
          backgroundColor: headerBackgroundColor,
          color: headerTextColor,
          padding: '16px',
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
        },
        rowStyle: {
          backgroundColor: '#ffffff',
          fontSize: 'var(--fs-12, 0.75rem)',
        },
        emptyRowsWhenPaging: false,
        pageSize: 10,
        pageSizeOptions: [10, 50, 100],
        actionsColumnIndex: -1,
      }}
      localization={{
        pagination: {
          labelRowsPerPage: '',
        },
      }}
      style={{
        backgroundColor: '#ededed',
        width: '80%',
      }}
      isLoading={supplierFixedRoutes.isLoading}
      actions={[
        {
          icon: () => <ClearSharpIcon fontSize="small" color="error" />,
          tooltip: 'Delete Route',
          onClick: (event: any, rowData: any) =>
            setConfirmDialog({
              title: 'Delete route?',
              subtitle: rowData.routeName,
              isOpen: true,
              onConfirm: () => onDelete(rowData),
            }),
        },
        {
          icon: () => <OpenInFullIcon fontSize="small" />,
          tooltip: 'View Route',
          onClick: (event: any, rowData: any) =>
            setFixedRoute(
              routes?.find((route) => route.id === rowData.id) ?? rowData,
            ),
        },
      ]}
    />
  )
}

export default FixedRoutesTable
